import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    FileCheck02
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { documentSchemaService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { DocumentSchema, CreateDocumentSchemaData, DocumentSchemaFilters, DocumentType } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';

export const DocumentSchemasPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [schemas, setSchemas] = useState<DocumentSchema[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [documentTypeFilter, setDocumentTypeFilter] = useState<DocumentType | ''>('');
    const [regionFilter, setRegionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<boolean | ''>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSchema, setEditingSchema] = useState<DocumentSchema | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [schemaToDelete, setSchemaToDelete] = useState<string | null>(null);

    const [formData, setFormData] = useState<CreateDocumentSchemaData>({
        document_type: 'bill_of_lading',
        schema_name: '',
        schema_code: '',
        version: '1.0',
        description: '',
        use_cases: '',
        region: '',
        country_codes: [],
        carrier_codes: [],
        is_default: false,
        is_active: true,
    });

    const documentTypeOptions = [
        { id: '', label: t('schemas.documentTypePlaceholder') },
        ...DOCUMENT_TYPES.map((dt) => ({ id: dt.key, label: dt.label })),
    ];

    const regionOptions = [
        { id: '', label: t('schemas.regionPlaceholder') },
        { id: 'asia', label: 'Asia' },
        { id: 'europe', label: 'Europa' },
        { id: 'americas', label: 'Americhe' },
        { id: 'africa', label: 'Africa' },
        { id: 'middle_east', label: 'Medio Oriente' },
    ];

    const statusOptions = [
        { id: '', label: t('schemas.statusPlaceholder') },
        { id: 'true', label: t('schemas.status.active') },
        { id: 'false', label: t('schemas.status.inactive') },
    ];

    useEffect(() => {
        loadSchemas();
    }, [searchTerm, documentTypeFilter, regionFilter, statusFilter]);

    const loadSchemas = () => {
        setIsLoading(true);
        const filters: DocumentSchemaFilters = {
            search: searchTerm || undefined,
            document_type: documentTypeFilter || undefined,
            region: regionFilter || undefined,
            is_active: statusFilter !== '' ? statusFilter === true : undefined,
        };
        const filtered = documentSchemaService.search(filters);
        setSchemas(filtered);
        setIsLoading(false);
    };

    const handleOpenModal = (schema?: DocumentSchema) => {
        if (schema) {
            setEditingSchema(schema);
            setFormData({
                document_type: schema.document_type,
                schema_name: schema.schema_name,
                schema_code: schema.schema_code,
                version: schema.version,
                description: schema.description,
                use_cases: schema.use_cases,
                region: schema.region,
                country_codes: schema.country_codes,
                carrier_codes: schema.carrier_codes,
                is_default: schema.is_default,
                is_active: schema.is_active,
            });
        } else {
            setEditingSchema(null);
            setFormData({
                document_type: 'bill_of_lading',
                schema_name: '',
                schema_code: '',
                version: '1.0',
                description: '',
                use_cases: '',
                region: '',
                country_codes: [],
                carrier_codes: [],
                is_default: false,
                is_active: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSchema(null);
    };

    const handleSave = () => {
        if (editingSchema) {
            documentSchemaService.update(editingSchema.document_schema_id, {
                ...formData,
                document_schema_id: editingSchema.document_schema_id,
            });
        } else {
            // Auto-generate schema_code from schema_name if not provided
            if (!formData.schema_code && formData.schema_name) {
                formData.schema_code = formData.schema_name
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '');
            }
            documentSchemaService.create(formData);
        }
        loadSchemas();
        handleCloseModal();
    };

    const handleDeleteClick = (id: string) => {
        setSchemaToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (schemaToDelete) {
            documentSchemaService.delete(schemaToDelete);
            loadSchemas();
            setIsDeleteModalOpen(false);
            setSchemaToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setSchemaToDelete(null);
    };

    const generateSchemaCode = (name: string) => {
        const code = name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
        setFormData({ ...formData, schema_code: code });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('schemas.title')}</h2>
                    <p className="text-tertiary mt-1">{t('schemas.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('schemas.newSchema')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Input
                        placeholder={t('schemas.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('schemas.documentTypePlaceholder')}
                        items={documentTypeOptions}
                        selectedKey={documentTypeFilter}
                        onSelectionChange={(key) => setDocumentTypeFilter(key as DocumentType | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('schemas.regionPlaceholder')}
                        items={regionOptions}
                        selectedKey={regionFilter}
                        onSelectionChange={(key) => setRegionFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('schemas.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter !== '' ? String(statusFilter) : ''}
                        onSelectionChange={(key) => setStatusFilter(key === '' ? '' : key === 'true')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Schemas Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('schemas.title')} (${schemas.length})`}
                    description={t('schemas.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('schemas.headers.documentType')} isRowHeader />
                        <Table.Head label={t('schemas.headers.schemaName')} />
                        <Table.Head label={t('schemas.headers.schemaCode')} />
                        <Table.Head label={t('schemas.headers.region')} />
                        <Table.Head label={t('schemas.headers.attributeCount')} />
                        <Table.Head label={t('schemas.headers.isDefault')} />
                        <Table.Head label={t('schemas.headers.isActive')} />
                        <Table.Head label={t('schemas.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {schemas.map((schema) => (
                            <Table.Row key={schema.document_schema_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">
                                        {DOCUMENT_TYPES.find((dt) => dt.key === schema.document_type)?.label || schema.document_type}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{schema.schema_name}</div>
                                    {schema.description && (
                                        <div className="text-sm text-tertiary">{schema.description}</div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary font-mono">{schema.schema_code}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{schema.region || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{schema.attribute_count}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    {schema.is_default ? (
                                        <Badge color="brand" size="sm">{t('schemas.status.default')}</Badge>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {schema.is_active ? (
                                        <Badge color="success" size="sm">{t('schemas.status.active')}</Badge>
                                    ) : (
                                        <Badge color="error" size="sm">{t('schemas.status.inactive')}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/admin/documents/schemas/${schema.document_schema_id}/attributes`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            {t('schemas.actions.viewAttributes')}
                                            <FileCheck02 className="size-4" />
                                        </Link>
                                        <Button
                                            onClick={() => handleOpenModal(schema)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('schemas.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteClick(schema.document_schema_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('schemas.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {schemas.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingSchema ? t('schemas.modal.edit') : t('schemas.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Select
                                            label={t('schemas.modal.fields.documentType')}
                                            items={documentTypeOptions.filter((opt) => opt.id !== '')}
                                            selectedKey={formData.document_type}
                                            onSelectionChange={(key) => setFormData({ ...formData, document_type: key as DocumentType })}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Input
                                            label={t('schemas.modal.fields.version')}
                                            placeholder={t('schemas.modal.fields.versionPlaceholder')}
                                            value={formData.version}
                                            onChange={(value) => setFormData({ ...formData, version: value })}
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('schemas.modal.fields.schemaName')}
                                        placeholder={t('schemas.modal.fields.schemaNamePlaceholder')}
                                        value={formData.schema_name}
                                        onChange={(value) => {
                                            setFormData({ ...formData, schema_name: value });
                                            if (!editingSchema) {
                                                generateSchemaCode(value);
                                            }
                                        }}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.schemaCode')}
                                        placeholder={t('schemas.modal.fields.schemaCodePlaceholder')}
                                        value={formData.schema_code}
                                        onChange={(value) => setFormData({ ...formData, schema_code: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.description')}
                                        placeholder={t('schemas.modal.fields.descriptionPlaceholder')}
                                        value={formData.description || ''}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.useCases')}
                                        placeholder={t('schemas.modal.fields.useCasesPlaceholder')}
                                        value={formData.use_cases || ''}
                                        onChange={(value) => setFormData({ ...formData, use_cases: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Select
                                            label={t('schemas.modal.fields.region')}
                                            items={regionOptions.filter((opt) => opt.id !== '')}
                                            selectedKey={formData.region || ''}
                                            onSelectionChange={(key) => setFormData({ ...formData, region: key as string })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Input
                                            label={t('schemas.modal.fields.countryCodes')}
                                            placeholder={t('schemas.modal.fields.countryCodesPlaceholder')}
                                            value={formData.country_codes?.join(', ') || ''}
                                            onChange={(value) =>
                                                setFormData({
                                                    ...formData,
                                                    country_codes: value.split(',').map((c) => c.trim()).filter(Boolean),
                                                })
                                            }
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('schemas.modal.fields.carrierCodes')}
                                        placeholder={t('schemas.modal.fields.carrierCodesPlaceholder')}
                                        value={formData.carrier_codes?.join(', ') || ''}
                                        onChange={(value) =>
                                            setFormData({
                                                ...formData,
                                                carrier_codes: value.split(',').map((c) => c.trim()).filter(Boolean),
                                            })
                                        }
                                        size="md"
                                    />

                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_default || false}
                                                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <span className="text-sm text-primary">{t('schemas.modal.fields.isDefault')}</span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active !== undefined ? formData.is_active : true}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <span className="text-sm text-primary">{t('schemas.modal.fields.isActive')}</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingSchema ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button onClick={handleDeleteCancel} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleDeleteConfirm} size="md" color="primary-destructive">
                                        {t('modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
